import React, { PropsWithChildren, useMemo, useState } from 'react';
import {
	SecurityFeatureBaseProps,
	buildSecurityRankingDeepLink,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/securityFeatures.utils.ts';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { EditableInfoContainer } from '@dealstack/frontend/shared/components/EditableInfoContainer/EditableInfoContainer.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { FormProvider } from 'react-hook-form';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { EditableInfoContainerGrid } from '@dealstack/frontend/shared/components/EditableInfoContainer/styled.tsx';

import { useMutation } from '@tanstack/react-query';
import {
	deleteSecurityCatchUpRights,
	updateSecurityCatchUpRights,
} from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import {
	CATCH_UP_RIGHTS_FORM_FIELD,
	getCatchUpRightsBasisLabel,
	makeCatchUpRightsOptions,
	useCatchUpRightsController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/CatchUpRights/useCatchUpRightsController.ts';
import { SecurityCatchUpRightsFormDto } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/CatchUpRights/catchUpRights.zod.ts';
import { DetailEditableField } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/DetailEditableField.tsx';
import { NumberInput, Select } from 'react-hook-form-mantine';

import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { numberToUiPercent } from '@dealstack/shared/lib/format/number.ts';
import { SecurityFeatureType } from '@dealstack/shared/lib/generated';
import { EditableInfoField } from '@dealstack/frontend/shared/components/EditableInfoField/EditableInfoField.tsx';
import { SecurityRankField } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityRankField/SecurityRankField.tsx';
import { useNavigate } from 'react-router-dom';

export const CatchUpRights = ({
	security,
	onCancel,
	refreshDetails,
	hasEditorPermission = true,
	relativeRanksMap,
}: PropsWithChildren<SecurityFeatureBaseProps>) => {
	const tCommonUI = useTranslationCommonUi();
	const tCapManage = useTranslationCapManage();
	const { catchUpRightsForm, resetForm } = useCatchUpRightsController(security);
	const [isEdit, setIsEdit] = useState(!security?.securityCatchUpRights);
	const navigate = useNavigate();
	const navigateToRanking = () =>
		navigate(buildSecurityRankingDeepLink(security?.id, SecurityFeatureType.CATCH_UP));

	const deleteCatchUpRightsRequest = useMutation({
		mutationFn: deleteSecurityCatchUpRights,
	});
	const updateCatchUpRightsRequest = useMutation({
		mutationFn: updateSecurityCatchUpRights,
	});

	const isLoading = updateCatchUpRightsRequest.isPending || deleteCatchUpRightsRequest.isPending;

	const securityRank = useMemo(() => {
		if (!security) {
			return null;
		}

		const matchedSecRank = security.securityRanks?.find(
			(secRank) => secRank.featureType === SecurityFeatureType.CATCH_UP
		);

		return matchedSecRank
			? {
					...matchedSecRank,
					relativeSortOrder: relativeRanksMap?.get(matchedSecRank.rankId)
						?.relativeSortOrder,
				}
			: null;
	}, [security, relativeRanksMap]);

	const handleUpdateCatchUpRights = (args: SecurityCatchUpRightsFormDto) => {
		const securityId = security?.id ?? '';
		const { target, basis } = args;

		updateCatchUpRightsRequest.mutate(
			{
				securityId,
				body: {
					target,
					basis,
				},
			},
			{
				onSuccess: () => {
					showToastV2({
						title: tCapManage('toasts.success.catch_up_rights_updated_successfully'),
					});

					refreshDetails?.(securityId);
					setIsEdit(false);
				},
			}
		);
	};

	const handleDeleteCatchUpRights = () => {
		const securityId = security?.id ?? '';
		deleteCatchUpRightsRequest.mutate(securityId, {
			onSuccess: () => {
				showToastV2({
					title: tCapManage('toasts.success.catch_up_rights_deleted_successfully'),
				});
				refreshDetails?.(securityId);
				setIsEdit(false);
			},
		});
	};

	const handleCancel = () => {
		resetForm();
		onCancel?.();
	};

	return (
		<EditableInfoContainer
			title={tCapManage('securities.catch_up_rights.title')}
			isEdit={isEdit}
			setIsEdit={setIsEdit}
			disableButtons={isLoading}
			onUpdate={catchUpRightsForm.handleSubmit(handleUpdateCatchUpRights)}
			onRemove={handleDeleteCatchUpRights}
			onCancel={handleCancel}
			hideRemove={!security?.securityCatchUpRights?.id}
			hasEditorPermission={hasEditorPermission}
			updateBtnDataTestId={TEST_ELEMENT_IDS.CAP_SECURITY_DETAILS_FEATURE_ROW_UPDATE_BUTTON}
		>
			<FormProvider {...catchUpRightsForm}>
				{isLoading && <InlineLoader />}

				<form
					onSubmit={catchUpRightsForm.handleSubmit(handleUpdateCatchUpRights)}
					style={{ width: '100%' }}
				>
					<EditableInfoContainerGrid $gridColumns={4} $alignItems="start">
						<DetailEditableField
							editMode={isEdit}
							label={tCapManage('securities.catch_up_rights.target.label')}
							formInput={
								<NumberInput
									name={CATCH_UP_RIGHTS_FORM_FIELD.target}
									control={catchUpRightsForm.control}
									size="md"
									decimalScale={2}
									rightSection={<Icon variant="percentage" size="vlg" />}
									className="percentage-input"
									data-testid={TEST_ELEMENT_IDS.CAP_CATCH_UP_RIGHTS_TARGET_INPUT}
								/>
							}
							data={numberToUiPercent(
								security?.securityCatchUpRights?.target,
								true,
								2
							)}
						/>

						<DetailEditableField
							editMode={isEdit}
							label={tCapManage('securities.catch_up_rights.basis.label')}
							formInput={
								<Select
									name={CATCH_UP_RIGHTS_FORM_FIELD.basis}
									control={catchUpRightsForm.control}
									size="md"
									rightSection={<Icon variant="caret-down" size="lg" />}
									rightSectionProps={{
										style: { pointerEvents: 'none' },
									}}
									data={makeCatchUpRightsOptions(tCommonUI)}
									data-testid={TEST_ELEMENT_IDS.CAP_CATCH_UP_RIGHTS_BASIS_SELECT}
								/>
							}
							data={getCatchUpRightsBasisLabel(
								tCommonUI,
								security?.securityCatchUpRights?.basis
							)}
						/>

						<EditableInfoField
							editMode={isEdit}
							label={tCapManage('securities.catch_up_rights.rank.label')}
							formInput={
								<SecurityRankField
									unranked={!securityRank}
									rankName={securityRank?.rank.displayName}
									sortOrder={securityRank?.relativeSortOrder || 1}
									totalRanks={relativeRanksMap?.size}
									onClick={navigateToRanking}
								/>
							}
							customValueElement={
								<SecurityRankField
									unranked={!securityRank}
									rankName={securityRank?.rank.displayName}
									sortOrder={securityRank?.relativeSortOrder || 1}
									totalRanks={relativeRanksMap?.size}
									showContainer={false}
								/>
							}
						/>
					</EditableInfoContainerGrid>
				</form>
			</FormProvider>
		</EditableInfoContainer>
	);
};
